import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { money } from "@/lib/api";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import FullScreenLoader from "@/components/FullScreenLoader";
import { ArrowLeft, Plus, Minus, Loader2, Search, X } from "lucide-react";

export default function CreateOrder() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [customerId, setCustomerId] = useState(params.get("customer") || "");
  const [cart, setCart] = useState({}); // product_id -> qty
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    Promise.all([api.get("/store/customers"), api.get("/store/products")]).then(([c, p]) => {
      setCustomers(c.data); setProducts(p.data); setReady(true);
    });
  }, []);

  if (!ready) return <FullScreenLoader />;

  const setQty = (pid, q) => setCart((c) => { const n = { ...c }; if (q <= 0) delete n[pid]; else n[pid] = q; return n; });
  const lines = Object.entries(cart).map(([pid, qty]) => { const p = products.find((x) => x.id === pid); return { p, qty }; }).filter((l) => l.p);
  const subtotal = lines.reduce((s, l) => s + l.p.base_price * l.qty, 0);
  const filtered = products.filter((p) => p.name.toLowerCase().includes(search.toLowerCase()));

  const place = async () => {
    if (!customerId) { toast.error("Select a customer"); return; }
    if (lines.length === 0) { toast.error("Add at least one product"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/store/orders", {
        customer_id: customerId,
        items: lines.map((l) => ({ product_id: l.p.id, quantity: l.qty })),
      });
      toast.success("Order created");
      navigate(`/dashboard/orders/${data.id}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setLoading(false);
  };

  return (
    <div className="space-y-6 max-w-4xl">
      <button onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground" data-testid="back-btn"><ArrowLeft className="h-4 w-4" /> Back</button>
      <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Create order</h1>
      <p className="text-sm text-muted-foreground -mt-4">Prices use the customer's price sheet automatically. (Base prices shown here.)</p>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-4">
          <Card className="p-5 rounded-xl border-border space-y-2">
            <Label>Customer</Label>
            <Select value={customerId} onValueChange={setCustomerId}>
              <SelectTrigger className="h-11" data-testid="order-customer-select"><SelectValue placeholder="Select a customer" /></SelectTrigger>
              <SelectContent>{customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} — {c.email}</SelectItem>)}</SelectContent>
            </Select>
          </Card>

          <Card className="rounded-xl border-border">
            <div className="p-4 border-b border-border relative">
              <Search className="absolute left-6 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search products to add..." className="pl-9 h-10" data-testid="order-product-search" />
            </div>
            <div className="divide-y divide-border max-h-96 overflow-y-auto">
              {filtered.map((p) => (
                <div key={p.id} className="flex items-center justify-between px-4 py-3">
                  <div className="min-w-0"><p className="text-sm font-medium truncate">{p.name}</p><p className="text-xs text-muted-foreground">{money(p.base_price)} · {p.quantity} in stock</p></div>
                  {cart[p.id] ? (
                    <div className="flex items-center border border-border rounded-lg">
                      <button onClick={() => setQty(p.id, cart[p.id] - 1)} className="p-2 hover:bg-secondary"><Minus className="h-3.5 w-3.5" /></button>
                      <span className="w-8 text-center text-sm">{cart[p.id]}</span>
                      <button onClick={() => setQty(p.id, cart[p.id] + 1)} className="p-2 hover:bg-secondary"><Plus className="h-3.5 w-3.5" /></button>
                    </div>
                  ) : (
                    <Button size="sm" variant="secondary" onClick={() => setQty(p.id, 1)} className="rounded-full h-8" data-testid={`order-add-${p.id}`}>Add</Button>
                  )}
                </div>
              ))}
            </div>
          </Card>
        </div>

        <div>
          <Card className="p-5 rounded-xl border-border sticky top-24">
            <h3 className="font-semibold mb-4">Order</h3>
            {lines.length === 0 ? <p className="text-sm text-muted-foreground">No products added.</p> : (
              <div className="space-y-2 mb-4 max-h-64 overflow-y-auto">
                {lines.map((l) => (
                  <div key={l.p.id} className="flex items-center justify-between text-sm">
                    <span className="truncate mr-2">{l.p.name} × {l.qty}</span>
                    <div className="flex items-center gap-2">
                      <span>{money(l.p.base_price * l.qty)}</span>
                      <button onClick={() => setQty(l.p.id, 0)} className="text-muted-foreground hover:text-destructive"><X className="h-3.5 w-3.5" /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div className="flex justify-between font-bold text-lg border-t border-border pt-3"><span>Subtotal</span><span>{money(subtotal)}</span></div>
            <Button onClick={place} disabled={loading} className="w-full h-11 rounded-full mt-4" data-testid="create-order-submit">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create order"}
            </Button>
          </Card>
        </div>
      </div>
    </div>
  );
}
